/**
 * Message List Component
 * Displays ALL chat messages with proper RTL/LTR support
 * 
 * CRITICAL: Renders ALL messages passed via props.
 * No filtering, no slicing, no conditional rendering.
 * Every message is displayed.
 */
import React from 'react';

const MessageList = ({ messages, language, isTyping }) => {
  /**
   * Determine if a language is RTL (Right-to-Left)
   * RTL languages: Persian (fa), Pashto (ps), Arabic (ar)
   * LTR languages: English (en), Turkish (tr)
   */
  const isRTL = (lang) => {
    return ['fa', 'ps', 'ar'].includes(lang);
  };

  /**
   * Get text direction for a message based on language
   */
  const getTextDirection = (lang) => {
    return isRTL(lang) ? 'rtl' : 'ltr';
  };

  const formatMessage = (content) => {
    if (!content) return '';
    // Simple formatting for line breaks
    return content.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < content.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const renderVideoLink = (metadata) => {
    if (metadata && metadata.has_video && metadata.video_url) {
      const textDirection = getTextDirection(language);
      return (
        <div 
          className="afgcoin-chatbot-video-link"
          dir={textDirection}
        >
          <a
            href={metadata.video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="afgcoin-chatbot-video-button"
            dir={textDirection}
          >
            📹 Watch Video Tutorial
          </a>
        </div>
      );
    }
    return null;
  };

  // Determine text direction for current language
  const textDirection = getTextDirection(language);

  // CRITICAL: Ensure messages is an array
  // Render ALL messages - no filtering, no slicing, no conditional rendering
  const messagesToRender = Array.isArray(messages) ? messages : [];

  const TypingIndicator = () => (
    <div 
      className="afgcoin-chatbot-message afgcoin-chatbot-message-assistant afgcoin-chatbot-message-typing"
      dir={textDirection}
    >
      <div className="afgcoin-chatbot-message-content">
        <div className="afgcoin-chatbot-typing-indicator">
          <span className="afgcoin-chatbot-typing-dot"></span>
          <span className="afgcoin-chatbot-typing-dot"></span>
          <span className="afgcoin-chatbot-typing-dot"></span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="afgcoin-chatbot-messages" dir={textDirection}>
      {messagesToRender.map((message, index) => {
        // CRITICAL: Render ALL messages, regardless of role, state, or type
        // No filtering by role, no slicing, no conditional rendering
        if (!message || !message.id) {
          console.warn('Invalid message detected, skipping:', message);
          return null;
        }

        // Check if previous message has same role (for styling only)
        const isConsecutive = index > 0 && 
                             messagesToRender[index - 1] && 
                             messagesToRender[index - 1].role === message.role;

        // Backend may send role "assistant" for human-agent messages (support/admin/agent) so they use the same style
        const roleForDisplay = message.role || 'user';
        return (
          <div
            key={message.id} // CRITICAL: Use message.id, never index
            className={`afgcoin-chatbot-message afgcoin-chatbot-message-${roleForDisplay} ${
              isConsecutive ? 'afgcoin-chatbot-message-consecutive' : ''
            }`}
            dir={textDirection}
          >
            <div 
              className="afgcoin-chatbot-message-content"
              dir={textDirection}
              style={{
                textAlign: isRTL(language) ? 'right' : 'left',
                unicodeBidi: 'plaintext'
              }}
            >
              {formatMessage(message.content || '')}
              {message.metadata && renderVideoLink(message.metadata)}
            </div>
          </div>
        );
      })}
      {/* Typing indicator - shown when AI or agent is typing */}
      {isTyping && <TypingIndicator />}
    </div>
  );
};

export default MessageList;

